import React, { useState, useMemo } from 'react';
import { ChartAreaSkeleton } from '../common/loading';

import {
  Box,
  Paper,
  Typography,
  SelectChangeEvent,
  alpha,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  IconButton,
  Button,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { VisibilityState } from '@tanstack/react-table';
import { TABLE_STYLES } from '../../constants/tableStyles';
import { utilizationPrimaryHeaderCellSx } from '../table/DataTable';
import {
  ColumnVisibilityMenu,
  type ColumnVisibilityMenuItem,
} from '../table/ColumnVisibilityMenu';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { SEARCE_COLORS } from '../../theme';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi } from '../../services/api';
import { FilterRequest } from '../../types/api';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  CertificationAnalysisChartTypeSelect,
  CertificationAnalysisViewToolbar,
  type CertificationAnalysisChartType,
} from './CertificationAnalysisChartToolbar';

interface CertificationsAnalysisProps {
  filters: FilterRequest;
  /** Flush layout when nested inside a tab (no outer padding or rounded border). */
  embedded?: boolean;
}

const CHART_COLORS = [
  SEARCE_COLORS.primary,
  '#9C27B0', // Purple
  '#E91E63', // Pink
  '#FF9800', // Orange
  '#4CAF50', // Green
  '#00BCD4', // Cyan
  '#673AB7', // Indigo
  '#009688', // Teal
];

type SortDirection = 'asc' | 'desc';
type ActiveCertSortColumn = 'certificationName' | 'count' | null;
type ExpiringCertSortColumn = 'certificationName' | 'count' | null;
type ProviderSortColumn = 'provider' | 'count' | null;

const analysisPrimaryHeaderCellSx: SxProps<Theme> = {
  ...utilizationPrimaryHeaderCellSx,
  fontWeight: 600,
};

const analysisCountPillBaseSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  ...TABLE_STYLES.chipInCell,
  px: 1.25,
  py: 0,
  borderRadius: '10px',
  fontWeight: 600,
  lineHeight: 1,
};

const PROVIDER_ANALYSIS_COLUMN_MENU_ITEMS: ColumnVisibilityMenuItem[] = [
  { id: 'provider', label: 'Service provider', hideable: false },
  { id: 'count', label: 'Active certifications' },
];

const EXPIRING_ANALYSIS_COLUMN_MENU_ITEMS: ColumnVisibilityMenuItem[] = [
  { id: 'certificationName', label: 'Certification name', hideable: false },
  { id: 'count', label: 'Certifications expiring (120d)' },
];

const ACTIVE_ANALYSIS_COLUMN_MENU_ITEMS: ColumnVisibilityMenuItem[] = [
  { id: 'certificationName', label: 'Certification name', hideable: false },
  { id: 'count', label: 'Active certifications' },
];

const analysisToolbarClearButtonSx: SxProps<Theme> = {
  color: SEARCE_COLORS.primary,
  borderColor: SEARCE_COLORS.primary,
  flexShrink: 0,
  '&:hover': {
    backgroundColor: alpha(SEARCE_COLORS.primary, 0.08),
    borderColor: SEARCE_COLORS.primary,
  },
};

const CertificationsAnalysis: React.FC<CertificationsAnalysisProps> = ({ filters, embedded = false }) => {
  const [selectedChart, setSelectedChart] = useState<CertificationAnalysisChartType>('activeCertifications');
  
  // Table state for Active Certifications
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState<ActiveCertSortColumn>('count');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [filterText, setFilterText] = useState('');

  // Table state for Expiring Certifications
  const [expiringPage, setExpiringPage] = useState(0);
  const [expiringRowsPerPage, setExpiringRowsPerPage] = useState(10);
  const [expiringSortColumn, setExpiringSortColumn] = useState<ExpiringCertSortColumn>('count');
  const [expiringSortDirection, setExpiringSortDirection] = useState<SortDirection>('desc');
  const [expiringFilterText, setExpiringFilterText] = useState('');

  // Table state for Service Providers
  const [providerPage, setProviderPage] = useState(0);
  const [providerRowsPerPage, setProviderRowsPerPage] = useState(10);
  const [providerSortColumn, setProviderSortColumn] = useState<ProviderSortColumn>('count');
  const [providerSortDirection, setProviderSortDirection] = useState<SortDirection>('desc');
  const [providerFilterText, setProviderFilterText] = useState('');

  const [providerAnalysisColumnVisibility, setProviderAnalysisColumnVisibility] = useState<VisibilityState>({});
  const [activeAnalysisColumnVisibility, setActiveAnalysisColumnVisibility] = useState<VisibilityState>({});
  const [expiringAnalysisColumnVisibility, setExpiringAnalysisColumnVisibility] = useState<VisibilityState>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['certification-analytics', filters],
    queryFn: () => dashboardApi.getCertificationAnalytics(filters),
    staleTime: 5 * 60 * 1000,
  });

  const handleChartChange = (event: SelectChangeEvent<string>) => {
    setSelectedChart(event.target.value as CertificationAnalysisChartType);
    // Reset table state when switching tabs
    setPage(0);
    setFilterText('');
    setExpiringPage(0);
    setExpiringFilterText('');
    setProviderPage(0);
    setProviderFilterText('');
  };

  const chartTypeSelect = (
    <CertificationAnalysisChartTypeSelect value={selectedChart} onChange={handleChartChange} />
  );

  /** In embedded tab layout, stretch to fill panel height instead of fixed 400px. */
  const embedPanel = embedded
    ? { flex: 1, minHeight: 0, height: '100%', width: '100%' }
    : { height: 400 };

  /** Pixel height so ResponsiveContainer's % height resolves; flex parents often leave % height at 0. */
  const TOP_CERTIFICATIONS_CHART_HEIGHT = embedded ? 420 : 360;

  // Filter and sort active certifications
  const filteredAndSortedActiveCerts = useMemo(() => {
    if (!data?.activeCertifications) return [];
    
    let filtered = data.activeCertifications.filter((cert) =>
      cert.certificationName.toLowerCase().includes(filterText.toLowerCase())
    );

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aVal: string | number = a[sortColumn === 'certificationName' ? 'certificationName' : 'count'];
        let bVal: string | number = b[sortColumn === 'certificationName' ? 'certificationName' : 'count'];
        
        if (sortColumn === 'certificationName') {
          aVal = (aVal as string).toLowerCase();
          bVal = (bVal as string).toLowerCase();
        }
        
        if (sortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    return filtered;
  }, [data?.activeCertifications, filterText, sortColumn, sortDirection]);

  // Paginated data
  const paginatedActiveCerts = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredAndSortedActiveCerts.slice(start, start + rowsPerPage);
  }, [filteredAndSortedActiveCerts, page, rowsPerPage]);

  const handleSort = (column: ActiveCertSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and sort expiring certifications
  const filteredAndSortedExpiringCerts = useMemo(() => {
    if (!data?.expiringCertifications) return [];
    
    let filtered = data.expiringCertifications.filter((cert) =>
      cert.certificationName.toLowerCase().includes(expiringFilterText.toLowerCase())
    );

    if (expiringSortColumn) {
      filtered = [...filtered].sort((a, b) => {
        let aVal: string | number = a[expiringSortColumn === 'certificationName' ? 'certificationName' : 'count'];
        let bVal: string | number = b[expiringSortColumn === 'certificationName' ? 'certificationName' : 'count'];
        
        if (expiringSortColumn === 'certificationName') {
          aVal = (aVal as string).toLowerCase();
          bVal = (bVal as string).toLowerCase();
        }
        
        if (expiringSortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    return filtered;
  }, [data?.expiringCertifications, expiringFilterText, expiringSortColumn, expiringSortDirection]);

  // Paginated data for expiring certifications
  const paginatedExpiringCerts = useMemo(() => {
    const start = expiringPage * expiringRowsPerPage;
    return filteredAndSortedExpiringCerts.slice(start, start + expiringRowsPerPage);
  }, [filteredAndSortedExpiringCerts, expiringPage, expiringRowsPerPage]);

  const handleExpiringSort = (column: ExpiringCertSortColumn) => {
    if (expiringSortColumn === column) {
      setExpiringSortDirection(expiringSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setExpiringSortColumn(column);
      setExpiringSortDirection('desc');
    }
    setExpiringPage(0);
  };

  const handleExpiringChangePage = (event: unknown, newPage: number) => {
    setExpiringPage(newPage);
  };

  const handleExpiringChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExpiringRowsPerPage(parseInt(event.target.value, 10));
    setExpiringPage(0);
  };

  // Filter and sort service providers
  const filteredAndSortedProviders = useMemo(() => {
    if (!data?.byServiceProvider) return [];
    
    let filtered = data.byServiceProvider.filter((provider: any) =>
      provider.provider.toLowerCase().includes(providerFilterText.toLowerCase())
    );

    if (providerSortColumn) {
      filtered = [...filtered].sort((a: any, b: any) => {
        let aVal: string | number = a[providerSortColumn === 'provider' ? 'provider' : 'count'];
        let bVal: string | number = b[providerSortColumn === 'provider' ? 'provider' : 'count'];
        
        if (providerSortColumn === 'provider') {
          aVal = (aVal as string).toLowerCase();
          bVal = (bVal as string).toLowerCase();
        }
        
        if (providerSortDirection === 'asc') {
          return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
        } else {
          return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
        }
      });
    }

    return filtered;
  }, [data?.byServiceProvider, providerFilterText, providerSortColumn, providerSortDirection]);

  // Paginated data for service providers
  const paginatedProviders = useMemo(() => {
    const start = providerPage * providerRowsPerPage;
    return filteredAndSortedProviders.slice(start, start + providerRowsPerPage);
  }, [filteredAndSortedProviders, providerPage, providerRowsPerPage]);

  const handleProviderSort = (column: ProviderSortColumn) => {
    if (providerSortColumn === column) {
      setProviderSortDirection(providerSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setProviderSortColumn(column);
      setProviderSortDirection('desc');
    }
    setProviderPage(0);
  };

  const handleProviderChangePage = (event: unknown, newPage: number) => {
    setProviderPage(newPage);
  };

  const handleProviderChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setProviderRowsPerPage(parseInt(event.target.value, 10));
    setProviderPage(0);
  };

  const renderByProviderChart = () => {
    const showProviderCountColumn = providerAnalysisColumnVisibility.count !== false;
    const providerTableColSpan = showProviderCountColumn ? 2 : 1;
    if (!data?.byServiceProvider || data.byServiceProvider.length === 0) {
      return (
        <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
          <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              No service provider data available
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
        <CertificationAnalysisViewToolbar
          chartSelect={chartTypeSelect}
          searchField={
            <TextField
              size="small"
              fullWidth
              placeholder="Search service providers..."
              value={providerFilterText}
              onChange={(e) => {
                setProviderFilterText(e.target.value);
                setProviderPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: SEARCE_COLORS.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: providerFilterText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setProviderFilterText('');
                        setProviderPage(0);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          }
          toolbarActions={
            <>
              {providerFilterText.trim() !== '' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setProviderFilterText('');
                    setProviderPage(0);
                  }}
                  sx={analysisToolbarClearButtonSx}
                >
                  Clear filters
                </Button>
              )}
              <ColumnVisibilityMenu
                items={PROVIDER_ANALYSIS_COLUMN_MENU_ITEMS}
                columnVisibility={providerAnalysisColumnVisibility}
                onColumnVisibilityChange={setProviderAnalysisColumnVisibility}
              />
            </>
          }
        />
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size={TABLE_STYLES.tableSize} sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={analysisPrimaryHeaderCellSx}>
                  <TableSortLabel
                    {...TABLE_STYLES.tableSortLabelProps}
                    active={providerSortColumn === 'provider'}
                    direction={providerSortColumn === 'provider' ? providerSortDirection : 'asc'}
                    onClick={() => handleProviderSort('provider')}
                    sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                  >
                    SERVICE PROVIDER
                  </TableSortLabel>
                </TableCell>
                {showProviderCountColumn && (
                  <TableCell align="right" sx={analysisPrimaryHeaderCellSx}>
                    <TableSortLabel
                      {...TABLE_STYLES.tableSortLabelProps}
                      active={providerSortColumn === 'count'}
                      direction={providerSortColumn === 'count' ? providerSortDirection : 'asc'}
                      onClick={() => handleProviderSort('count')}
                      sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                    >
                      ACTIVE CERTIFICATIONS
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProviders.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={providerTableColSpan}
                    align="center"
                    sx={{
                      ...(TABLE_STYLES.cell as object),
                      ...(TABLE_STYLES.cellEmpty as object),
                    }}
                  >
                    <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                      No service providers found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProviders.map((provider: any, index: number) => (
                  <TableRow key={index} hover sx={TABLE_STYLES.tableRowHover}>
                    <TableCell sx={TABLE_STYLES.cell}>{provider.provider}</TableCell>
                    {showProviderCountColumn && (
                      <TableCell align="right" sx={TABLE_STYLES.cell}>
                        <Box
                          component="span"
                          sx={{
                            ...analysisCountPillBaseSx,
                            backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                            color: SEARCE_COLORS.primary,
                          }}
                        >
                          {provider.count}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAndSortedProviders.length}
          page={providerPage}
          onPageChange={handleProviderChangePage}
          rowsPerPage={providerRowsPerPage}
          onRowsPerPageChange={handleProviderChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: `1px solid ${SEARCE_COLORS.neutral_200}` }}
        />
      </Box>
    );
  };

  const renderProgressChart = (items: any[], title: string) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            No {title.toLowerCase()} data available
          </Typography>
        </Box>
      );
    }

    const total = items.reduce((sum, item) => sum + (item.count || 0), 0);

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: SEARCE_COLORS.textPrimary, fontSize: '1rem' }}>
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {items.map((item: any, index: number) => {
            const percentage = total > 0 ? ((item.count || 0) / total) * 100 : 0;
            const color = CHART_COLORS[index % CHART_COLORS.length];
            
            return (
              <Box key={index} sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem' }}>
                    {item.level || item.type || 'Unknown'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: SEARCE_COLORS.textPrimary, fontSize: '0.875rem' }}>
                      {item.count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: SEARCE_COLORS.textSecondary, fontSize: '0.75rem' }}>
                      ({percentage.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    width: '100%',
                    height: 24,
                    backgroundColor: alpha(SEARCE_COLORS.neutral_200, 0.3),
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  <Box
                    sx={{
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: color,
                      borderRadius: '12px',
                      transition: 'width 0.5s ease-in-out',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      paddingRight: 1,
                    }}
                  >
                    {percentage > 10 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: SEARCE_COLORS.white,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      >
                        {percentage.toFixed(1)}%
                      </Typography>
                    )}
                  </Box>
                  {percentage <= 10 && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: SEARCE_COLORS.textSecondary,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                      }}
                    >
                      {percentage.toFixed(1)}%
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    );
  };

  const renderLevelsHorizontalChart = (levels: any[]) => {
    if (levels.length === 0) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            No level data available
          </Typography>
        </Box>
      );
    }

    // Transform data for horizontal bar chart
    const chartData = levels.map((level: any) => ({
      level: level.level.length > 50 ? level.level.substring(0, 50) + '...' : level.level,
      count: level.count,
      fullLevel: level.level,
    }));

    return (
      <Box sx={{ height: 300, overflow: 'hidden', mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: SEARCE_COLORS.textPrimary, fontSize: '1rem' }}>
          Certification Levels
        </Typography>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 30, bottom: 50 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
            <XAxis 
              type="number" 
              tick={{ fontSize: 12, fill: SEARCE_COLORS.textSecondary }}
              label={{ 
                value: 'Active Certifications', 
                position: 'insideBottom', 
                offset: -5, 
                style: { 
                  textAnchor: 'middle', 
                  fill: SEARCE_COLORS.textSecondary,
                  fontSize: 12
                } 
              }}
            />
            <YAxis
              dataKey="level"
              type="category"
              width={150}
              tick={{ fontSize: 11, fill: SEARCE_COLORS.textSecondary }}
            />
            <RechartsTooltip
              formatter={(value: number) => [value, 'Active Certifications']}
              labelFormatter={(label: any) => {
                const dataPoint = chartData.find((d: any) => d.level === label);
                return dataPoint ? dataPoint.fullLevel : label;
              }}
              labelStyle={{ color: SEARCE_COLORS.textPrimary, fontWeight: 600 }}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderByLevelsChart = () => {
    const levels = data?.byTypesAndLevels?.levels || [];
    const types = data?.byTypesAndLevels?.types || [];

    if (levels.length === 0 && types.length === 0) {
      return (
        <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column', px: 2 }}>
          <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              No certification data available
            </Typography>
          </Box>
        </Box>
      );
    }

    return (
      <Box sx={{ ...embedPanel, overflow: 'auto', px: 2, display: 'flex', flexDirection: 'column' }}>
        <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {levels.length > 0 && renderLevelsHorizontalChart(levels)}
          {types.length > 0 && renderProgressChart(types, 'Certification Types')}
        </Box>
      </Box>
    );
  };

  const renderByTypesChart = () => {
    const types = data?.byTypesAndLevels?.types || [];

    if (types.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            ...embedPanel,
          }}
        >
          <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
            No type data available
          </Typography>
        </Box>
      );
    }

    // Transform data for horizontal bar chart
    const chartData = types.map((type: any) => ({
      type: type.type.length > 20 ? type.type.substring(0, 20) + '...' : type.type,
      count: type.count,
      fullType: type.type,
    }));

    return (
      <Box
        sx={{
          ...embedPanel,
          overflow: 'hidden',
          ...(embedded ? { minHeight: 400 } : {}),
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
            <XAxis
              dataKey="type"
              tick={{ fontSize: 12, fill: SEARCE_COLORS.textSecondary }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: SEARCE_COLORS.textSecondary }}
              label={{ value: 'Active Certifications', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: SEARCE_COLORS.textSecondary } }}
            />
            <RechartsTooltip
              formatter={(value: number) => [value, 'Active Certifications']}
              labelFormatter={(label: any) => {
                const dataPoint = chartData.find((d: any) => d.type === label);
                return dataPoint ? dataPoint.fullType : label;
              }}
              labelStyle={{ color: SEARCE_COLORS.textPrimary, fontWeight: 600 }}
            />
            <Bar dataKey="count" radius={[4, 4, 0, 0]}>
              {chartData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderTopCertificationsChart = () => {
    if (!data?.topCertifications || data.topCertifications.length === 0) {
      return (
        <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
          <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              No certification data available
            </Typography>
          </Box>
        </Box>
      );
    }

    const chartData = data.topCertifications.map((cert: any) => {
      const name = String(cert.certificationName ?? cert.certification_name ?? '').trim() || 'Unknown';
      return {
        ...cert,
        certificationName: name,
        shortName: name.length > 50 ? `${name.substring(0, 50)}...` : name,
        count: Number(cert.count ?? 0),
      };
    });

    return (
      <Box sx={{ ...embedPanel, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
        <Box
          sx={{
            width: '100%',
            flexShrink: 0,
            height: TOP_CERTIFICATIONS_CHART_HEIGHT,
            minHeight: TOP_CERTIFICATIONS_CHART_HEIGHT,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(SEARCE_COLORS.neutral_300, 0.3)} />
            <XAxis 
              type="number" 
              tick={{ fontSize: 12, fill: SEARCE_COLORS.textSecondary }}
              label={{ fontSize: 12, value: 'Active Certifications', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: SEARCE_COLORS.textSecondary } }}
            />
            <YAxis
              dataKey="shortName"
              type="category"
              width={150}
              tick={{ fontSize: 11, fill: SEARCE_COLORS.textSecondary }}
            />
            <RechartsTooltip
              formatter={(value: number) => [value, 'Active Certifications']}
              labelFormatter={(label: any) => {
                const dataPoint = chartData.find((d: any) => d.shortName === label);
                return dataPoint ? dataPoint.certificationName : label;
              }}
              labelStyle={{ color: SEARCE_COLORS.textPrimary, fontWeight: 600 }}
            />
            <Bar dataKey="count" radius={[0, 4, 4, 0]}>
              {chartData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        </Box>
      </Box>
    );
  };

  const renderExpiringCertificationsChart = () => {
    if (!data?.expiringCertifications || data.expiringCertifications.length === 0) {
      return (
        <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
          <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              No expiring certifications in the next 3 months
            </Typography>
          </Box>
        </Box>
      );
    }

    const showExpiringCountColumn = expiringAnalysisColumnVisibility.count !== false;
    const expiringTableColSpan = showExpiringCountColumn ? 2 : 1;

    return (
      <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
        <CertificationAnalysisViewToolbar
          chartSelect={chartTypeSelect}
          searchField={
            <TextField
              size="small"
              fullWidth
              placeholder="Search certifications..."
              value={expiringFilterText}
              onChange={(e) => {
                setExpiringFilterText(e.target.value);
                setExpiringPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: SEARCE_COLORS.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: expiringFilterText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setExpiringFilterText('');
                        setExpiringPage(0);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          }
          toolbarActions={
            <>
              {expiringFilterText.trim() !== '' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setExpiringFilterText('');
                    setExpiringPage(0);
                  }}
                  sx={analysisToolbarClearButtonSx}
                >
                  Clear filters
                </Button>
              )}
              <ColumnVisibilityMenu
                items={EXPIRING_ANALYSIS_COLUMN_MENU_ITEMS}
                columnVisibility={expiringAnalysisColumnVisibility}
                onColumnVisibilityChange={setExpiringAnalysisColumnVisibility}
              />
            </>
          }
        />
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size={TABLE_STYLES.tableSize} sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={analysisPrimaryHeaderCellSx}>
                  <TableSortLabel
                    {...TABLE_STYLES.tableSortLabelProps}
                    active={expiringSortColumn === 'certificationName'}
                    direction={expiringSortColumn === 'certificationName' ? expiringSortDirection : 'asc'}
                    onClick={() => handleExpiringSort('certificationName')}
                    sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                  >
                    CERTIFICATION NAME
                  </TableSortLabel>
                </TableCell>
                {showExpiringCountColumn && (
                  <TableCell align="right" sx={analysisPrimaryHeaderCellSx}>
                    <TableSortLabel
                      {...TABLE_STYLES.tableSortLabelProps}
                      active={expiringSortColumn === 'count'}
                      direction={expiringSortColumn === 'count' ? expiringSortDirection : 'asc'}
                      onClick={() => handleExpiringSort('count')}
                      sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                    >
                      CERTIFICATIONS EXPIRING (Next 120 Days)
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedExpiringCerts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={expiringTableColSpan}
                    align="center"
                    sx={{
                      ...(TABLE_STYLES.cell as object),
                      ...(TABLE_STYLES.cellEmpty as object),
                    }}
                  >
                    <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                      No certifications found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedExpiringCerts.map((cert: any, index: number) => (
                  <TableRow key={index} hover sx={TABLE_STYLES.tableRowHover}>
                    <TableCell sx={TABLE_STYLES.cell}>{cert.certificationName}</TableCell>
                    {showExpiringCountColumn && (
                      <TableCell align="right" sx={TABLE_STYLES.cell}>
                        <Box
                          component="span"
                          sx={{
                            ...analysisCountPillBaseSx,
                            backgroundColor:
                              cert.count === 1
                                ? alpha(SEARCE_COLORS.error, 0.1)
                                : alpha(SEARCE_COLORS.warning, 0.1),
                            color: cert.count === 1 ? SEARCE_COLORS.error : SEARCE_COLORS.warning,
                          }}
                        >
                          {cert.count}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAndSortedExpiringCerts.length}
          page={expiringPage}
          onPageChange={handleExpiringChangePage}
          rowsPerPage={expiringRowsPerPage}
          onRowsPerPageChange={handleExpiringChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: `1px solid ${SEARCE_COLORS.neutral_200}` }}
        />
      </Box>
    );
  };

  const renderActiveCertificationsTable = () => {
    if (!data?.activeCertifications || data.activeCertifications.length === 0) {
      return (
        <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
          <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
              No active certifications available
            </Typography>
          </Box>
        </Box>
      );
    }

    const showActiveCountColumn = activeAnalysisColumnVisibility.count !== false;
    const activeTableColSpan = showActiveCountColumn ? 2 : 1;

    return (
      <Box sx={{ ...embedPanel, display: 'flex', flexDirection: 'column' }}>
        <CertificationAnalysisViewToolbar
          chartSelect={chartTypeSelect}
          searchField={
            <TextField
              size="small"
              fullWidth
              placeholder="Search certifications..."
              value={filterText}
              onChange={(e) => {
                setFilterText(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: SEARCE_COLORS.textSecondary }} />
                  </InputAdornment>
                ),
                endAdornment: filterText && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setFilterText('');
                        setPage(0);
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          }
          toolbarActions={
            <>
              {filterText.trim() !== '' && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setFilterText('');
                    setPage(0);
                  }}
                  sx={analysisToolbarClearButtonSx}
                >
                  Clear filters
                </Button>
              )}
              <ColumnVisibilityMenu
                items={ACTIVE_ANALYSIS_COLUMN_MENU_ITEMS}
                columnVisibility={activeAnalysisColumnVisibility}
                onColumnVisibilityChange={setActiveAnalysisColumnVisibility}
              />
            </>
          }
        />
        <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
          <Table stickyHeader size={TABLE_STYLES.tableSize} sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={analysisPrimaryHeaderCellSx}>
                  <TableSortLabel
                    {...TABLE_STYLES.tableSortLabelProps}
                    active={sortColumn === 'certificationName'}
                    direction={sortColumn === 'certificationName' ? sortDirection : 'asc'}
                    onClick={() => handleSort('certificationName')}
                    sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                  >
                    CERTIFICATION NAME
                  </TableSortLabel>
                </TableCell>
                {showActiveCountColumn && (
                  <TableCell align="right" sx={analysisPrimaryHeaderCellSx}>
                    <TableSortLabel
                      {...TABLE_STYLES.tableSortLabelProps}
                      active={sortColumn === 'count'}
                      direction={sortColumn === 'count' ? sortDirection : 'asc'}
                      onClick={() => handleSort('count')}
                      sx={TABLE_STYLES.primaryHeaderTableSortLabelSx}
                    >
                      ACTIVE CERTIFICATIONS
                    </TableSortLabel>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedActiveCerts.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={activeTableColSpan}
                    align="center"
                    sx={{
                      ...(TABLE_STYLES.cell as object),
                      ...(TABLE_STYLES.cellEmpty as object),
                    }}
                  >
                    <Typography variant="body2" sx={{ color: SEARCE_COLORS.textSecondary }}>
                      No certifications found matching your search
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedActiveCerts.map((cert: any, index: number) => (
                  <TableRow key={index} hover sx={TABLE_STYLES.tableRowHover}>
                    <TableCell sx={TABLE_STYLES.cell}>{cert.certificationName}</TableCell>
                    {showActiveCountColumn && (
                      <TableCell align="right" sx={TABLE_STYLES.cell}>
                        <Box
                          component="span"
                          sx={{
                            ...analysisCountPillBaseSx,
                            backgroundColor: alpha(SEARCE_COLORS.primary, 0.1),
                            color: SEARCE_COLORS.primary,
                          }}
                        >
                          {cert.count}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredAndSortedActiveCerts.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ borderTop: `1px solid ${SEARCE_COLORS.neutral_200}` }}
        />
      </Box>
    );
  };

  const renderChart = () => {
    switch (selectedChart) {
      case 'byProvider':
        return renderByProviderChart();
      case 'byLevels':
        return renderByLevelsChart();
      case 'byTypes':
        return renderByLevelsChart(); // Show combined view
      case 'topCertifications':
        return renderTopCertificationsChart();
      case 'expiringCertifications':
        return renderExpiringCertificationsChart();
      case 'activeCertifications':
        return renderActiveCertificationsTable();
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...(embedded
          ? {
              p: 0,
              border: 'none',
              borderRadius: 0,
              boxShadow: 'none',
              backgroundColor: SEARCE_COLORS.white,
              flex: 1,
              minHeight: 0,
              width: '100%',
              alignSelf: 'stretch',
            }
          : {
              p: 3,
              border: `1px solid ${SEARCE_COLORS.neutral_200}`,
              borderRadius: 2,
              backgroundColor: SEARCE_COLORS.white,
            }),
      }}
    >
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', ...embedPanel }}>
            <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 0 }}>
              <ChartAreaSkeleton titleWidth="40%" height={300} />
            </Box>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <CertificationAnalysisViewToolbar chartSelect={chartTypeSelect} />
            <Alert severity="error">Failed to load certification analytics data</Alert>
          </Box>
        ) : (
          renderChart()
        )}
      </Box>
    </Paper>
  );
};

export default CertificationsAnalysis;
